import React, { useContext, useEffect, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import {
  ItemStatusEnum,
  JobStatusEnum,
  JobTypeEnum,
  Pageable,
  PermissionTypesEnum,
  ReservationMultiDuplicationJobDetailOutDTO,
  ReservationMultiDuplicationJobItemOutDTO,
  ReservationMultiDuplicationJobItemsFilter,
  StateFilterEnum,
} from '@api/ringil-api.v2.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import useNavigation from '@hooks/useNavigation.tsx';
import { Link } from 'react-router-dom';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import COText from '@app/pages/COText/COText.tsx';
import COBadge from '@components/FigmaComponents/COBadge.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDataTable, { ColumnConfig, FilterConfig, FilterType, PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import JobDetailHeader, { JobExpiryNote } from '@components/ringil3/Features/Job/JobDetailHeader.tsx';
import SvgOK from '@icons/check-circle.svg?react';
import SvgAlert from '@icons/alert-circle.svg?react';
import SvgWarning from '@icons/alert.svg?react';
import SvgRobot from '@icons/ai-robot.svg?react';
import SvgLoader from '@app/icons/loader/Loader.tsx';
import { dayjsFromApiUtcInTz, formatDateFromDayjsWithLocale, stringHHmmFromDayjs } from '@utils/dateWithTz';
import { isNotNullOrUndefined } from '@utils/utils';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { useJobChannel } from '@hooks/useJobs/useJobChannel.ts';
import { formatJobItemError } from '@hooks/useBEValidation/formatBEValidationMessage';

interface ComponentProps {
  jobId: string;
}

const TERMINAL: JobStatusEnum[] = [JobStatusEnum.DONE, JobStatusEnum.ERROR, JobStatusEnum.CANCELLED];
// Reservations are created faster than a debounce window, so a debounced reload would starve until the
// whole burst ends. Throttle instead: reload at most this often while hints keep coming, trailing one included.
const TABLE_RELOAD_MIN_INTERVAL_MS = 1000;

/**
 * Job-type-specific detail for RESERVATION_MULTI_DUPLICATION ("Opakující se rezervace" run as a job).
 * One GET for the job-level card; the item table is a classic RingilDataTable paged/filtered on the BE
 * (a job may hold up to 1000 slots). Live source is the per-job Centrifugo channel (works for the
 * creator and colleagues alike); while running the page also polls — 15 s when push is down, 60 s as
 * insurance when it is up — and refetches once more when the job finishes.
 */
const ReservationMultiDuplicationJobDetail: React.FC<ComponentProps> = ({ jobId }) => {
  const { getReservationMultiDuplicationJobDetail, getReservationMultiDuplicationJobItems, cancelAsyncJob, retryJob, archiveJob } = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const nav = useNavigation();
  const [detail, setDetail] = useState<ReservationMultiDuplicationJobDetailOutDTO>();
  // Key of the job action in flight (RD-2954). The clicked button spins (RingilButton swallows clicks
  // while `loading`) and the other action buttons grey out, so neither a double click nor a parallel
  // action sends a second request — that used to come back from the BE as a 409.
  // The key is cleared only once the detail GET that follows the mutation has landed: on a fast
  // (local) backend the POST can return before the second click of a double click, and until the
  // refetched status re-renders the page the old button is still there and would fire again.
  const [action, setAction] = useState<string | null>(null);
  const busy = (key: string) => ({ loading: action === key, disabled: action != null && action !== key });
  const abortControllerRef = useRef<AbortController | null>(null);
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: ReservationMultiDuplicationJobItemOutDTO[]; clear: () => void }>(null);
  const reloadTimer = useRef<number | null>(null);
  const lastTableReload = useRef(0);

  const jobsRwFlag = useSecuredFlag('jobs-rw-actions', {
    kind: 'action',
    description: 'Retry / archive jobs',
    permissions: [PermissionTypesEnum.PERMISSION_JOBS_RW],
  });

  const load = (onLoaded?: () => void) => {
    getReservationMultiDuplicationJobDetail(jobId, d => {
      if (abortControllerRef.current?.signal.aborted) return;
      setDetail(d);
      onLoaded?.();
    });
  };

  // Item push events reload the paged table: immediately on the first hint, then at most once per
  // interval while the burst lasts (the pending timer collects every further hint into one reload).
  const throttledTableReload = () => {
    if (reloadTimer.current != null) return;
    const wait = Math.max(0, lastTableReload.current + TABLE_RELOAD_MIN_INTERVAL_MS - Date.now());
    reloadTimer.current = window.setTimeout(() => {
      reloadTimer.current = null;
      lastTableReload.current = Date.now();
      tableRef.current?.reloadData();
    }, wait);
  };

  const reloadAll = (onLoaded?: () => void) => {
    load(onLoaded);
    throttledTableReload();
  };

  useEffect(() => {
    const ac = new AbortController();
    abortControllerRef.current = ac;
    load();
    return () => {
      ac.abort();
      if (reloadTimer.current != null) window.clearTimeout(reloadTimer.current);
    };
  }, [jobId]);

  const { live } = useJobChannel(jobId, {
    onProgress: e => {
      setDetail(prev => {
        if (prev == null) return prev;
        return {
          ...prev,
          job: {
            ...prev.job,
            jobStatus: (e.status as JobStatusEnum) ?? prev.job.jobStatus,
            processedCount: e.processed ?? prev.job.processedCount,
            successCount: e.success ?? prev.job.successCount,
            failedCount: e.failed ?? prev.job.failedCount,
            totalCount: e.total ?? prev.job.totalCount,
          },
        };
      });
      throttledTableReload();
    },
    onFinished: () => reloadAll(),
  });

  const status = detail?.job?.jobStatus;
  const isRunning = status != null && !TERMINAL.includes(status);
  const isTerminal = status != null && TERMINAL.includes(status);

  // Poll fallback: push hints are the primary signal; polling covers a dropped hint (60 s) or a
  // disconnected push gateway (15 s). Nothing polls once the job is terminal.
  useEffect(() => {
    if (detail == null || !isRunning) return;
    const id = setInterval(reloadAll, live ? 60000 : 15000);
    return () => clearInterval(id);
  }, [jobId, isRunning, live, detail == null]);

  const loadDataApi = (
    filter: ReservationMultiDuplicationJobItemsFilter,
    pageable: Pageable,
    onSuccess: (pagedData: PagedData<ReservationMultiDuplicationJobItemOutDTO>) => void,
  ) => {
    getReservationMultiDuplicationJobItems(jobId, filter, pageable, data => onSuccess(data));
  };

  const stateLabel = (state: StateFilterEnum) =>
    ({
      [StateFilterEnum.WITH_ERROR]: tr('JobDetail.reservationDuplication.state.WITH_ERROR', 'S chybou'),
      [StateFilterEnum.OK]: tr('JobDetail.reservationDuplication.state.OK', 'V pořádku'),
    })[state];

  const statusIcon = (row: ReservationMultiDuplicationJobItemOutDTO) => {
    if (row.itemStatus === ItemStatusEnum.DONE && isNotNullOrUndefined(row.reservationId)) return <SvgOK className={'text-complementary-success shrink-0'} />;
    if (row.itemStatus === ItemStatusEnum.ERROR) return <SvgAlert className={'text-complementary-error shrink-0'} />;
    if (row.itemStatus === ItemStatusEnum.RUNNING) return <SvgLoader className={'animate-spin shrink-0'} />;
    return null;
  };

  const renderSlotColumn = (row: ReservationMultiDuplicationJobItemOutDTO) => {
    const tz = row.timezoneId ?? 'Europe/Prague';
    const since = row.since != null ? dayjsFromApiUtcInTz(row.since, tz) : null;
    const till = row.till != null ? dayjsFromApiUtcInTz(row.till, tz) : null;
    return (
      <div className={'flex flex-row gap-2 items-start'}>
        {statusIcon(row)}
        <div className={'flex flex-col gap-1'}>
          <COBadge variant={'outlined'} type={'neutral'}>
            <span className={'text-content-secondary'}>{tr('MultiduplicationJobTable.rowId', 'ID:')}</span>
            &nbsp;{row.sortOrder + 1}
          </COBadge>
          {since != null && <span className={'text-content-primary'}>{formatDateFromDayjsWithLocale(since)}</span>}
          {since != null && till != null && (
            <span className={'text-content-secondary text-sm whitespace-nowrap'}>
              {stringHHmmFromDayjs(since)} - {stringHHmmFromDayjs(till)}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderCreatedColumn = (row: ReservationMultiDuplicationJobItemOutDTO) => {
    if (row.itemStatus === ItemStatusEnum.DONE && isNotNullOrUndefined(row.reservationId)) {
      return (
        <div className={'flex flex-col gap-1 items-start'}>
          <Link className={'text-content-brand font-medium'} to={nav.createNavigationLink(nav.urlFunctions.createReservationDetail(row.reservationId))}>
            {row.reservationApplicationId}
          </Link>
          {detail?.createdByName != null && <span className={'text-sm text-content-tertiary'}>{detail.createdByName}</span>}
        </div>
      );
    }
    if (row.itemStatus === ItemStatusEnum.ERROR) {
      const errorText = formatJobItemError(row.error, row.errorMessage, tr);
      return (
        <div className={'flex flex-col gap-1'}>
          <COText label={tr('JobDetail.reservationDuplication.notCreated', 'Nevytvořeno')} />
          {isNotNullOrUndefined(errorText) && <span className={'text-content-tertiary text-sm'}>{errorText}</span>}
        </div>
      );
    }
    if (row.itemStatus === ItemStatusEnum.RUNNING) {
      return <COText label={tr('JobDetail.item.inProgress', 'In progress')} />;
    }
    if (row.itemStatus === ItemStatusEnum.SKIPPED) {
      return <span className={'text-content-tertiary'}>{tr('JobDetail.item.skipped', 'Skipped')}</span>;
    }
    return <COText label={tr('JobDetail.item.waiting', 'Waiting')} />;
  };

  const renderSourceColumn = () => {
    const source = detail?.source;
    if (source == null) return <div />;
    const place = [source.locationName, source.rampName].filter(t => t != null).join(', ');
    return (
      <div className={'flex flex-col gap-1'}>
        <Link to={nav.createNavigationLink(nav.urlFunctions.createReservationDetail(source.reservationId))}>
          {source.applicationId ?? tr('JobDetail.reservationDuplication.sourceReservation', 'Výchozí rezervace')}
        </Link>
        {place.length > 0 && <span className={'text-sm text-content-tertiary'}>{place}</span>}
      </div>
    );
  };

  const columnConfig: ColumnConfig<ReservationMultiDuplicationJobItemOutDTO>[] = [
    { id: 'slot', header: 'JobDetail.reservationDuplication.table.slot', headerDefault: 'Termín', width: '30%', order: 1, render: renderSlotColumn },
    { id: 'created', header: 'JobDetail.reservationDuplication.table.created', headerDefault: 'Vytvořená rezervace', width: '40%', order: 2, render: renderCreatedColumn },
    { id: 'duplicatedFrom', header: 'MultiduplicationV3.table.duplicatedFrom', headerDefault: 'Zduplikováno z', width: '30%', order: 3, render: renderSourceColumn },
  ];

  const filtersConf: FilterConfig[] = [
    {
      filterType: FilterType.Dropdown,
      placeholder: tr('JobDetail.reservationDuplication.filterState', 'Status'),
      label: tr('JobDetail.reservationDuplication.filterState', 'Status'),
      value: ['stateFilter'],
      options: Object.values(StateFilterEnum).map(state => ({ label: stateLabel(state), value: state, active: true })),
      dataTestId: 'reservation-duplication-job-filter-state',
    },
  ];

  if (detail == null) {
    return <SvgLoader className={'animate-spin'} />;
  }

  const { processedCount: processed, failedCount: failed, totalCount: total } = detail.job;
  const applicationId = detail.job.applicationId;
  const progressPct = total != null && total > 0 ? Math.min(Math.round((processed / total) * 100), 100) : 0;
  const canRetry = status === JobStatusEnum.ERROR || (status === JobStatusEnum.DONE && failed > 0);
  const isDone = status === JobStatusEnum.DONE;
  const okCount = Math.max((total ?? 0) - failed, 0);
  const title = `${tr('JobDetail.task', 'Úkol')} ${applicationId ?? ''}`;

  const retry = () => {
    setAction('retry');
    retryJob(
      jobId,
      () => reloadAll(() => setAction(null)),
      null,
      {
        successToast: false,
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const cancel = () => {
    setAction('cancel');
    cancelAsyncJob(
      jobId,
      () => load(() => setAction(null)),
      null,
      {
        onError: (_e, showStandardError) => {
          setAction(null);
          showStandardError();
        },
      },
    );
  };

  const archive = () => {
    setAction('archive');
    archiveJob(jobId, () => nav.navigate(nav.urlFunctions.createJobList()), null, {
      successToast: false,
      onError: (_e, showStandardError) => {
        setAction(null);
        showStandardError();
      },
    });
  };

  return (
    <div className={'flex flex-col gap-4'}>
      <BreadCrumbComponent
        items={[
          { label: tr('JobList.title', 'Rozpracované úkoly'), url: nav.createNavigationLink(nav.urlFunctions.createJobList()) },
          { label: title, disabled: true },
        ]}
      />

      <JobDetailHeader
        title={title}
        jobTypeLabel={tr(`JobList.typeBadge.${JobTypeEnum.RESERVATION_MULTI_DUPLICATION}`, 'Duplikace')}
        jobCategory={detail.jobCategory}
        createdByName={detail.createdByName}
        createdAt={detail.job.createdAt}
        onCancel={isRunning ? cancel : undefined}
        onArchive={isTerminal && jobsRwFlag.granted ? archive : undefined}
        busyAction={action}
      />

      {isRunning && (
        <div className={'flex flex-col gap-4'}>
          <COInlineMessage variant={'warning'} contentClassName={'flex-1 text-center'}>
            {tr('JobDetail.reservationDuplication.inProgress', 'Rezervace se vytvářejí')}
          </COInlineMessage>
          <div className={'flex flex-col items-center gap-4 rounded bg-white p-8'}>
            <SvgLoader className={'animate-spin'} />
            <span className={'text-content-secondary'}>{tr('JobList.processedPct', '{{pct}} % zpracováno', { pct: progressPct })}</span>
            <div className={'w-60 bg-base-quaternary rounded h-2'}>
              <div className={'bg-content-brand h-2 rounded'} style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        </div>
      )}

      {/* Same robot card as the expedition (V3) multiduplication detail: one sentence covers both the all-ok
          and the partial outcome, the item table below carries the per-slot detail. */}
      {isDone && (
        <div className={'flex flex-col items-center gap-4 rounded bg-white p-6'}>
          <div className={'flex flex-row items-center gap-3'}>
            <SvgRobot width={32} height={32} />
            {failed > 0 ? (
              <>
                <SvgWarning className={'text-complementary-warning shrink-0'} />
                <span>
                  {tr('JobDetail.reservationDuplication.robotPartial', '{{failed}} rezervací se nepodařilo vytvořit, dalších {{ok}} rezervací bylo úspěšně vytvořeno.', {
                    failed,
                    ok: okCount,
                  })}
                </span>
              </>
            ) : (
              <>
                <SvgOK className={'text-complementary-success shrink-0'} />
                <span>{tr('JobDetail.reservationDuplication.robotAllOk', 'Všech {{count}} rezervací bylo úspěšně vytvořeno.', { count: total })}</span>
              </>
            )}
          </div>
          <RingilButton
            mode={'filled'}
            label={tr('JobDetail.reservationDuplication.showCreated', 'Zobrazit rezervace')}
            dataTestId={'reservation-duplication-job-show-created'}
            onClick={() => nav.navigate(nav.urlFunctions.createReservationList())}
          />
        </div>
      )}

      {status === JobStatusEnum.ERROR && (
        <COInlineMessage variant={'error'} contentClassName={'flex-1 text-center'}>
          <div>{tr('JobDetail.reservationDuplication.failed', 'Rezervace se nepodařilo vytvořit.')}</div>
          {detail.job.errorMessage != null && <div>{detail.job.errorMessage}</div>}
        </COInlineMessage>
      )}
      {status === JobStatusEnum.CANCELLED && (
        <COInlineMessage variant={'info'} icon={<SvgAlert className="text-content-tertiary" />}>
          {tr('JobDetail.reservationDuplication.cancelled', 'Úkol byl zrušen. Rezervace vytvořené před zrušením zůstávají.')}
        </COInlineMessage>
      )}

      {canRetry && jobsRwFlag.granted && (
        <div className={'flex flex-col items-center gap-4 rounded bg-white p-8'}>
          <RingilButton mode={'filled'} label={tr('JobList.retry', 'Zkusit znovu')} dataTestId={'reservation-duplication-job-retry'} onClick={retry} {...busy('retry')} />
        </div>
      )}

      <RingilDataTable
        ref={tableRef}
        columnConfig={columnConfig}
        filtersConf={filtersConf}
        loadDataFromApi={loadDataApi}
        filterName={'reservation_duplication_job_items'}
      />

      {isTerminal && <JobExpiryNote expiresAt={detail.expiresAt} />}
    </div>
  );
};

export default ReservationMultiDuplicationJobDetail;
